import React, { useState, useMemo, useEffect } from 'react';
import { createConsumer } from '@rails/actioncable';
import { Head, Link, router, useForm } from '@inertiajs/react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  ArrowLeft, 
  Upload, 
  Trash2, 
  Edit, 
  FileText,
  FileJson,
  Hash,
  Code,
  Database,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Loader2
} from 'lucide-react';
import { Breadcrumbs } from '@/components/ui/breadcrumbs';
import { PaginatedTableControls } from '@/components/ui/paginated-table';

interface Chunk {
  id: number;
  page_number: number;
  content: string;
  citation?: string;
  embed?: string;
  metadata?: Record<string, any>;
}

interface Document {
  id: number;
  name: string;
}

interface ChunksIndexProps {
  document: Document;
  chunks: Chunk[];
  current_page?: number;
  total_pages?: number;
  per_page?: number;
  total_count?: number;
}

type SortField = 'page_number' | 'size' | 'none';
type SortDirection = 'asc' | 'desc';

export default function ChunksIndex({ 
  document, 
  chunks, 
  current_page = 1,
  total_pages = 1,
  per_page = 50,
  total_count = 0
}: ChunksIndexProps) {
  const [selectedChunks, setSelectedChunks] = useState<Set<number>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [sortField, setSortField] = useState<SortField>('none');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [uploadProgress, setUploadProgress] = useState<{
    isUploading: boolean;
    progress: number;
    message: string;
    jobId?: string;
  }>({
    isUploading: false,
    progress: 0,
    message: '',
    jobId: undefined
  });
  const [importProgress, setImportProgress] = useState<{
    isImporting: boolean;
    message: string;
    jobId?: string;
  }>({
    isImporting: false,
    message: '',
    jobId: undefined
  });
  const [selectedJsonFile, setSelectedJsonFile] = useState<File | null>(null);

  // ActionCable connection for real-time progress updates
  useEffect(() => {
    const jobId = uploadProgress.jobId;
    if (!jobId) return;

    const cable = (window as any).App?.cable ?? createConsumer();
    const subscription = cable.subscriptions.create(
      { channel: 'JobProgressChannel', job_id: jobId },
      {
        received: (data: { message: string; timestamp: number }) => {
          const message = data.message;
          let progress = 0;

          const progressMatch = message.match(/(\d+(?:\.\d+)?%)/);
          if (progressMatch) {
            progress = parseFloat(progressMatch[1]);
          }

          setUploadProgress(prev => ({
            ...prev,
            progress,
            message
          }));

          if (message.includes('completed') || message.includes('ERROR:')) {
            setUploadProgress(prev => ({
              ...prev,
              isUploading: false,
              jobId: undefined
            }));

            if (message.includes('completed')) {
              router.reload({ only: ['chunks'] });
            }
          }
        }
      }
    );

    return () => {
      cable.subscriptions.remove(subscription);
    };
  }, [uploadProgress.jobId]);

  useEffect(() => {
    const jobId = importProgress.jobId;
    if (!jobId) return;

    const cable = (window as any).App?.cable ?? createConsumer();
    const subscription = cable.subscriptions.create(
      { channel: 'JobProgressChannel', job_id: jobId },
      {
        received: (data: { message: string; timestamp: number }) => {
          const message = data.message;

          setImportProgress(prev => ({
            ...prev,
            message
          }));

          const importFailed = message.includes('ERROR:');
          const importSucceeded = message.startsWith('Import completed!');

          if (importFailed || importSucceeded) {
            setImportProgress(prev => ({
              ...prev,
              isImporting: false,
              jobId: undefined
            }));

            if (importSucceeded) {
              setSelectedJsonFile(null);
              router.reload({ only: ['chunks', 'total_count', 'total_pages', 'current_page'] });
            }
          }
        }
      }
    );

    return () => {
      cable.subscriptions.remove(subscription);
    };
  }, [importProgress.jobId]);

  const { post } = useForm();

  const getSizeColor = (length: number) => {
    if (length < 300) return 'bg-red-100 text-red-800';
    if (length < 2000) return 'bg-yellow-100 text-yellow-800';
    if (length > 4000) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  const sortedChunks = useMemo(() => {
    if (sortField === 'none') return chunks;
    
    return [...chunks].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'page_number':
          aValue = a.page_number;
          bValue = b.page_number;
          break;
        case 'size':
          aValue = a.content.length;
          bValue = b.content.length;
          break;
        default:
          return 0;
      }
      
      const result = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      return sortDirection === 'desc' ? -result : result;
    });
  }, [chunks, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3 w-3 opacity-50" />;
    }
    return sortDirection === 'asc' ? 
      <ArrowUp className="h-3 w-3" /> : 
      <ArrowDown className="h-3 w-3" />;
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedChunks(new Set(sortedChunks.map(chunk => chunk.id)));
    } else {
      setSelectedChunks(new Set());
    }
  };

  const handleSelectChunk = (chunkId: number, checked: boolean) => {
    const newSelected = new Set(selectedChunks);
    if (checked) {
      newSelected.add(chunkId);
    } else {
      newSelected.delete(chunkId);
    }
    setSelectedChunks(newSelected);
    setSelectAll(newSelected.size === sortedChunks.length && sortedChunks.length > 0);
  };

  const handleUploadChunks = () => {
    setUploadProgress({
      isUploading: true,
      progress: 0,
      message: 'Starting vectorization...',
      jobId: undefined
    });
    
    // Get CSRF token
    const csrfToken = (window.document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content || '';
    
    // Use fetch instead of Inertia post to handle JSON response
    fetch(`/documents/${document.id}/upload_document_chunks`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return response.json();
    })
    .then(data => {
      if (data.status === 'success' && data.job_id) {
        setUploadProgress(prev => ({
          ...prev,
          jobId: data.job_id,
          message: data.message || 'Vectorization started...'
        }));
      } else {
        throw new Error(data.message || 'Upload failed');
      }
    })
    .catch(error => {
      console.error('Upload error:', error);
      setUploadProgress({
        isUploading: false,
        progress: 0,
        message: 'Upload failed: ' + error.message,
        jobId: undefined
      });
    });
  };

  const handleImportJson = () => {
    if (!selectedJsonFile) {
      alert('Please select a JSON file first');
      return;
    }

    setImportProgress({
      isImporting: true,
      message: 'Starting chunk import...',
      jobId: undefined
    });

    const csrfToken = (window.document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content || '';
    const formData = new FormData();
    formData.append('file', selectedJsonFile);

    fetch(`/documents/${document.id}/chunks/import_json`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken
      },
      body: formData
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(data => {
            throw new Error(data.message || `HTTP ${response.status}: ${response.statusText}`);
          });
        }
        return response.json();
      })
      .then(data => {
        if (data.status === 'success' && data.job_id) {
          setImportProgress(prev => ({
            ...prev,
            jobId: data.job_id,
            message: data.message || 'Chunk import started...'
          }));
        } else {
          throw new Error(data.message || 'Import failed');
        }
      })
      .catch(error => {
        console.error('Import error:', error);
        setImportProgress({
          isImporting: false,
          message: 'Import failed: ' + error.message,
          jobId: undefined
        });
      });
  };

  const handleDeleteSelected = () => {
    if (selectedChunks.size === 0) return;
    
    if (confirm('Are you sure you want to delete the selected chunks?')) {
      post(`/documents/${document.id}/chunks/destroy_multiple`, {
        data: { chunk_ids: Array.from(selectedChunks) },
        onSuccess: () => {
          setSelectedChunks(new Set());
          setSelectAll(false);
        }
      });
    }
  };

  const handleDeleteChunk = (chunkId: number) => {
    if (confirm('Are you sure you want to delete this chunk?')) {
      router.delete(`/documents/${document.id}/chunks/${chunkId}`);
    }
  };


  return (
    <>
      <Head title={`Chunks for ${document.name}`} />
      
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="space-y-6">
          {/* Breadcrumbs */}
          <Breadcrumbs opsConsole
            items={[
              { label: document.name, href: `/documents/${document.id}` },
              { label: 'Chunks', current: true }
            ]}
          />

          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <h1 className="text-3xl font-bold text-center lg:text-left">
              Chunks for {document.name}
            </h1>
            
            <Button asChild variant="outline">
              <Link href={`/documents/${document.id}`}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Document
              </Link>
            </Button>
          </div>

          {/* Import chunks from JSON */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileJson className="h-5 w-5" />
                Import Chunks from JSON
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Upload a JSON file exported from an external chunking app. Imported chunks are appended to this document.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 items-end">
                <div className="flex-1">
                  <label className="text-sm font-medium mb-2 block">JSON file</label>
                  <input
                    type="file"
                    accept=".json,application/json"
                    disabled={importProgress.isImporting}
                    onChange={(e) => setSelectedJsonFile(e.target.files?.[0] ?? null)}
                    className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                  />
                </div>
                <Button
                  onClick={handleImportJson}
                  disabled={!selectedJsonFile || importProgress.isImporting}
                >
                  {importProgress.isImporting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FileJson className="h-4 w-4 mr-2" />
                  )}
                  {importProgress.isImporting ? 'Importing...' : 'Import Chunks'}
                </Button>
              </div>
              {importProgress.message && (
                <p className={`text-sm ${importProgress.message.includes('ERROR:') ? 'text-red-600' : 'text-gray-600'}`}>
                  {importProgress.message}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Upload Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Upload Chunks to Vector DB
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={handleUploadChunks}
                disabled={uploadProgress.isUploading}
                className="bg-green-600 hover:bg-green-700"
              >
                {uploadProgress.isUploading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                {uploadProgress.isUploading ? 'Uploading...' : 'Upload Chunks to Vector DB'}
              </Button>
            </CardContent>
          </Card>

          {/* Bulk Actions */}
          <Card>
            <CardContent className="p-4">
              <Button 
                variant="destructive"
                onClick={handleDeleteSelected}
                disabled={selectedChunks.size === 0}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected Chunks ({selectedChunks.size})
              </Button>
            </CardContent>
          </Card>

          {/* Chunks Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectAll}
                          onCheckedChange={handleSelectAll}
                        />
                      </TableHead>
                      <TableHead className="w-20">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleSort('page_number')}
                          className="h-auto p-0 font-semibold hover:bg-transparent"
                        >
                          <div className="flex items-center gap-2">
                            <Hash className="h-4 w-4" />
                            Page #
                            {getSortIcon('page_number')}
                          </div>
                        </Button>
                      </TableHead>
                      <TableHead className="w-20">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleSort('size')}
                          className="h-auto p-0 font-semibold hover:bg-transparent"
                        >
                          <div className="flex items-center gap-2">
                            Size
                            {getSortIcon('size')}
                          </div>
                        </Button>
                      </TableHead>
                      <TableHead className="w-48">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Citation
                        </div>
                      </TableHead>
                      <TableHead className="w-64">Content</TableHead>
                      <TableHead className="w-48">
                        <div className="flex items-center gap-2">
                          <Code className="h-4 w-4" />
                          Embed
                        </div>
                      </TableHead>
                      <TableHead className="w-32">
                        <div className="flex items-center gap-2">
                          <Database className="h-4 w-4" />
                          Metadata
                        </div>
                      </TableHead>
                      <TableHead className="w-32">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedChunks.map((chunk) => (
                      <TableRow key={chunk.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedChunks.has(chunk.id)}
                            onCheckedChange={(checked) => handleSelectChunk(chunk.id, checked as boolean)}
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          {chunk.page_number}
                        </TableCell>
                        <TableCell>
                          <Badge className={getSizeColor(chunk.content.length)}>
                            {chunk.content.length}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-48 break-words">
                          <div className="text-xs font-mono text-gray-600 whitespace-pre-wrap">
                            {chunk.citation || '—'}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-64 break-words">
                          <div className="text-sm text-gray-800 whitespace-pre-wrap max-h-32 overflow-y-auto">
                            {chunk.content}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-48 break-words">
                          <div className="text-xs font-mono text-gray-600 whitespace-pre-wrap max-h-24 overflow-y-auto">
                            {chunk.embed || '—'}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-32 break-words">
                          <div className="text-xs font-mono text-gray-600 whitespace-pre-wrap max-h-24 overflow-y-auto">
                            {chunk.metadata && Object.keys(chunk.metadata).length > 0 
                              ? JSON.stringify(chunk.metadata, null, 2)
                              : '—'
                            }
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-2">
                            <Button asChild size="sm" variant="outline">
                              <Link href={`/documents/${document.id}/chunks/${chunk.id}/edit`}>
                                <Edit className="h-3 w-3 mr-1" />
                                Edit
                              </Link>
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => handleDeleteChunk(chunk.id)}
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <PaginatedTableControls
                currentPage={current_page}
                totalPages={total_pages}
                perPage={per_page}
                totalCount={total_count}
                baseUrl={`/documents/${document.id}/chunks`}
                className="p-4 border-t"
              />

              {chunks.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No chunks found for this document.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}